import React, { useCallback, useEffect, useState } from 'react';
import { planApi, formatMoney, formatDateTime, INTERVAL_LABELS } from './planApi';
import { Badge, ConfirmModal, Field } from './PlanUi';

const STATUS_OPTIONS = [
  ['', 'All'],
  ['refund_required', 'Refund required'],
  ['paid', 'Paid (not applied yet)'],
  ['fulfilled', 'Fulfilled'],
  ['created', 'Awaiting payment'],
  ['refunded', 'Refunded'],
  ['expired', 'Expired'],
  ['failed', 'Failed'],
];
const TONE = {
  fulfilled: 'good', paid: 'info', fulfilling: 'info', created: 'warn', refund_required: 'bad',
  refunded: 'muted', expired: 'muted', cancelled: 'muted', failed: 'bad',
};
const KIND = { new: 'New', upgrade: 'Upgrade', renewal: 'Renewal' };

// Owner self-serve Cashfree payments for plans
const SubscriptionPayments = ({ notify }) => {
  const [status, setStatus] = useState('refund_required');
  const [orderId, setOrderId] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [refundFor, setRefundFor] = useState(null);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try {
      setData(await planApi.listPayments({ status, orderId: orderId.trim(), page, limit: 20 }));
      setError('');
    } catch (err) {
      setError(err.message);
    }
  }, [status, orderId, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [status, orderId]);

  const reconcile = async (p) => {
    setBusyId(p._id);
    try {
      const res = await planApi.reconcilePayment(p._id);
      notify(`Order ${p.cfOrderId}: ${res.payment.status}`);
      load();
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setBusyId(null);
    }
  };

  const markRefunded = async () => {
    setBusyId(refundFor._id);
    try {
      await planApi.markRefunded(refundFor._id, note);
      notify('Marked as refunded');
      setRefundFor(null);
      setNote('');
      load();
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      {data?.needsAttention > 0 && (
        <div className="pm-callout pm-callout--warn">
          {data.needsAttention} payment(s) need attention. <strong>Refund required</strong> means the owner paid but the plan could not be applied
          (e.g. they paid twice from two tabs, or the amount didn’t match). Issue the refund in the Cashfree dashboard, then mark it refunded here.
        </div>
      )}

      <div className="pm-toolbar">
        <div className="pm-toolbar__filters">
          <select className="pm-input pm-input--sm" value={status} onChange={e => setStatus(e.target.value)}>
            {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input className="pm-input" value={orderId} onChange={e => setOrderId(e.target.value)} placeholder="Search Cashfree order ID (RPS_…)" />
        </div>
        <button className="pm-btn pm-btn--ghost" onClick={load}>Refresh</button>
      </div>

      {error && <div className="pm-callout pm-callout--error">{error}</div>}

      <div className="pm-table-wrap">
        <table className="pm-table">
          <thead>
            <tr><th>Created</th><th>Owner</th><th>Purchase</th><th>Amount</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {!data && <tr><td colSpan={6} className="pm-empty-cell"><span className="pm-spinner" /> Loading…</td></tr>}
            {data?.payments.length === 0 && <tr><td colSpan={6} className="pm-empty-cell">No payments found.</td></tr>}
            {data?.payments.map(p => (
              <tr key={p._id} style={{ cursor: 'default' }}>
                <td className="pm-small">{formatDateTime(p.createdAt)}<div className="pm-mono pm-muted">{p.cfOrderId}</div></td>
                <td><strong>{p.owner?.name || '—'}</strong><div className="pm-muted pm-small">{p.owner?.email}</div></td>
                <td>{KIND[p.kind]} · {p.planName} <span className="pm-muted">v{p.planVersion}</span><div className="pm-muted pm-small">{INTERVAL_LABELS[p.interval]}</div></td>
                <td className="pm-mono">{formatMoney(p.quote.totalMinor)}
                  {p.quote.prorationCreditMinor > 0 && <div className="pm-muted pm-small">credit −{formatMoney(p.quote.prorationCreditMinor)}</div>}
                </td>
                <td>
                  <Badge tone={TONE[p.status]}>{p.status.replace(/_/g, ' ')}</Badge>
                  {p.fulfillmentError && <div className="pm-small" style={{ color: 'var(--pm-bad)' }}>{p.fulfillmentError}</div>}
                  {p.adminNote && <div className="pm-small pm-muted">{p.adminNote}</div>}
                  {p.paymentMethod && <div className="pm-small pm-muted">{p.paymentMethod.toUpperCase()}{p.bankReference ? ` · ${p.bankReference}` : ''}</div>}
                </td>
                <td>
                  <div className="pm-inline-form">
                    {['created', 'superseded', 'paid'].includes(p.status) && (
                      <button className="pm-btn pm-btn--ghost pm-btn--sm" disabled={busyId === p._id} onClick={() => reconcile(p)}>Re-check</button>
                    )}
                    {p.status === 'refund_required' && (
                      <button className="pm-btn pm-btn--ghost pm-btn--sm" disabled={busyId === p._id} onClick={() => { setRefundFor(p); setNote(''); }}>Mark refunded</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data?.pagination.pages > 1 && (
        <div className="pm-pagination">
          <button className="pm-btn pm-btn--ghost pm-btn--sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span className="pm-muted pm-small">Page {data.pagination.page} of {data.pagination.pages}</span>
          <button className="pm-btn pm-btn--ghost pm-btn--sm" disabled={page >= data.pagination.pages} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}

      {refundFor && (
        <ConfirmModal
          title="Mark payment as refunded"
          confirmLabel="Mark refunded"
          busy={busyId === refundFor._id}
          onClose={() => setRefundFor(null)}
          onConfirm={markRefunded}
          message={`Confirm you have refunded ${formatMoney(refundFor.quote.totalMinor)} for order ${refundFor.cfOrderId} in the Cashfree dashboard.`}
        >
          <Field label="Refund reference / note (required)">
            <input className="pm-input" value={note} onChange={e => setNote(e.target.value)} maxLength={500} placeholder="e.g. Cashfree refund ID" />
          </Field>
        </ConfirmModal>
      )}
    </div>
  );
};

export default SubscriptionPayments;
